/**
 * CloudClient - thin fetch wrapper for the SketchChair API
 * (CLOUD_SYNC_SPEC.md §7). Attaches a fresh Firebase ID token as a Bearer
 * header; throws typed errors so the UI can react (pop sign-in on 401).
 */
import { API_BASE } from './config.js';

export class AuthRequiredError extends Error {
    constructor() {
        super('Sign in to use cloud features');
        this.name = 'AuthRequiredError';
    }
}

export class ApiError extends Error {
    constructor(status, body) {
        super(body?.detail || body?.error || `API error ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.code = body?.error || null;
    }
}

export class CloudClient {
    /** @param {import('./AuthService.js').AuthService} auth */
    constructor(auth) {
        this.auth = auth;
        this.base = API_BASE;
    }

    async _headers(json = true) {
        const headers = {};
        if (json) headers['Content-Type'] = 'application/json';
        const token = await this.auth.getIdToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
        return headers;
    }

    /** JSON request; throws AuthRequiredError on 401, ApiError otherwise */
    async request(method, path, body) {
        const res = await fetch(this.base + path, {
            method,
            headers: await this._headers(body !== undefined),
            body: body !== undefined ? JSON.stringify(body) : undefined
        });
        if (res.status === 401) throw new AuthRequiredError();
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new ApiError(res.status, data);
        return data;
    }

    get(path) { return this.request('GET', path); }
    post(path, body) { return this.request('POST', path, body); }
    put(path, body) { return this.request('PUT', path, body); }
    patch(path, body) { return this.request('PATCH', path, body); }
    delete(path) { return this.request('DELETE', path); }

    /** Authenticated binary GET returning an object URL (thumbnails) */
    async blobUrl(path) {
        const res = await fetch(this.base + path, {
            headers: await this._headers(false)
        });
        if (!res.ok) return null;
        return URL.createObjectURL(await res.blob());
    }
}
